import { useMemo } from 'react';
import type {
  DescriptiveAnswer, DescriptivePartAnswer, MainSheetType, Question, StrokesBlock, TextBlock
} from '../types/models';
import { InkPad, SheetBudgetItem } from './InkPad';
import RichTextView from './RichTextView';
import { emptyStrokesPage } from '../utils/answers';

/** The exam's answer-sheet provisioning, resolved for the question being answered. */
export interface SheetProvisioning {
  mainSheet: MainSheetType;
  sheetBudget: SheetBudgetItem[];
  questionNo?: string;
}

interface Props {
  question: Question;
  value: DescriptiveAnswer;
  onChange: (next: DescriptiveAnswer) => void;
  // Answer-sheet provisioning for this exam (omitted in read-only/grading views).
  sheets?: SheetProvisioning;
}

/** Sub-part label: (a), (b), (c)… */
export const partLetter = (i: number) => String.fromCharCode(97 + i);

type Slot = { pages: StrokesBlock[]; blocks: TextBlock[] };

/** The ink + typed-text editor for one descriptive answer slot (a whole question, or one part). */
function DescriptiveSlotEditor({ hint, pages, blocks, canAddInk, onChange, sheets }: {
  hint?: string;
  pages: StrokesBlock[];
  blocks: TextBlock[];
  canAddInk: boolean;
  onChange: (next: Slot) => void;
  sheets?: SheetProvisioning;
}) {
  return (
    <div className="answer-editor">
      {hint && <div className="muted">{hint}</div>}
      {canAddInk && (
        <InkPad
          pages={pages}
          onChange={next => onChange({ pages: next, blocks })}
          mainSheet={sheets?.mainSheet}
          sheetBudget={sheets?.sheetBudget}
          questionNo={sheets?.questionNo}
        />
      )}
    </div>
  );
}

/** A descriptive question split into individually-answered sub-parts (a)/(b)/(c)…. */
function MultiPartEditor({ question, value, onChange, sheets }: Props) {
  const parts = question.parts;
  const byId = useMemo(
    () => new Map((value.parts ?? []).map(p => [p.partId, p])),
    [value.parts]
  );

  const emptySlot = (partId: string): DescriptivePartAnswer => ({
    partId,
    pages: [emptyStrokesPage()],
    blocks: [],
  });
  const slotFor = (partId: string): DescriptivePartAnswer => byId.get(partId) ?? emptySlot(partId);

  function updateSlot(partId: string, next: Slot) {
    // Rebuild the full parts list (one entry per question part) so order and untouched parts persist.
    const nextParts = parts.map(p =>
      p.id === partId
        ? { partId, pages: next.pages, blocks: next.blocks }
        : (byId.get(p.id) ?? emptySlot(p.id)));
    onChange({ ...value, pages: [], blocks: [], parts: nextParts });
  }

  return (
    <div>
      <div className="muted" style={{ marginBottom: 4 }}>
        This question has {parts.length} parts — answer each part in its own box below.
      </div>
      {parts.map((p, i) => {
        const slot = slotFor(p.id);
        return (
          <div key={p.id} className="card"
               style={{ background: 'var(--surface-2)', marginBottom: 12, padding: 12 }}>
            {/* Keep this part's question pinned to the top of the scrolling answer area. */}
            <div style={{
              position: 'sticky', top: 0, zIndex: 4,
              background: 'var(--surface-2)', marginBottom: 10, paddingBottom: 8,
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontWeight: 700 }}>({partLetter(i)})</div>
                <div className="muted" style={{ fontSize: 12 }}>{p.marks} mark{p.marks > 1 ? 's' : ''}</div>
              </div>
              <RichTextView html={p.prompt} style={{ marginTop: 6 }} />
            </div>
            <DescriptiveSlotEditor
              pages={slot.pages}
              blocks={slot.blocks}
              canAddInk={question.allowInk}
              onChange={next => updateSlot(p.id, next)}
              sheets={sheets}
            />
          </div>
        );
      })}
    </div>
  );
}

export function AnswerEditor({ question, value, onChange, sheets }: Props) {
  const hasParts = (question.parts?.length ?? 0) > 0;

  const hint = useMemo(() => {
    if (question.allowInk && !question.allowText) return 'Write your answer below. Each page is A4-sized — use “Add page” when you need more room.';
    if (question.allowText && !question.allowInk) return 'Type your answer below.';
    return 'Write or type your answer. Each ink page is A4-sized — use “Add page” when you need more room.';
  }, [question.allowText, question.allowInk]);

  if (hasParts) return <MultiPartEditor question={question} value={value} onChange={onChange} sheets={sheets} />;

  return (
    <DescriptiveSlotEditor
      hint={hint}
      pages={value.pages}
      blocks={value.blocks}
      canAddInk={question.allowInk}
      onChange={next => onChange({ ...value, pages: next.pages, blocks: next.blocks })}
      sheets={sheets}
    />
  );
}
